import { useEffect, useId, type ReactNode } from 'react';

import { ApiError } from '../api/client';

export function Card({
  title,
  actions,
  children,
  footer,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-header">
          <h2>{title}</h2>
          {actions}
        </header>
      )}
      {children}
      {footer}
    </section>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="card stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint !== undefined && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="spinner" aria-hidden="true" />
      {label ?? 'Cargando…'}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/**
 * Muestra un error de la API. Separa el mensaje del `routerDetail` porque son cosas
 * distintas: "el router rechazó la operación" es la conclusión, y el texto crudo del
 * MikroTik es lo que hace falta para arreglarlo.
 */
export function ErrorAlert({ error, onDismiss }: { error: unknown; onDismiss?: () => void }) {
  if (!error) return null;

  const isApi = error instanceof ApiError;
  const message = error instanceof Error ? error.message : String(error);
  const detail = isApi ? error.routerDetail : undefined;

  return (
    <div className="alert error" role="alert">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <strong>{message}</strong>
        {onDismiss && (
          <button type="button" className="ghost small" onClick={onDismiss}>
            Cerrar
          </button>
        )}
      </div>
      {detail && <div className="detail">{detail}</div>}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const titleId = useId();

  // Escape cierra el diálogo: es lo que espera cualquiera que abra un modal por error.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={wide ? 'modal wide' : 'modal'} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="modal-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="ghost small" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <div className="pagination">
      <span>
        {total} resultado{total === 1 ? '' : 's'} · página {page} de {totalPages}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="small" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ← Anterior
        </button>
        <button
          type="button"
          className="small"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Siguiente →
        </button>
      </span>
    </div>
  );
}

/** Copia al portapapeles y confirma en el propio botón. */
export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  return (
    <button
      type="button"
      className="small"
      onClick={async (event) => {
        const button = event.currentTarget;
        try {
          await navigator.clipboard.writeText(value);
          const original = button.textContent;
          button.textContent = '¡Copiado!';
          setTimeout(() => {
            button.textContent = original;
          }, 1200);
        } catch {
          // `clipboard` requiere contexto seguro (HTTPS o localhost). Si no está, se
          // selecciona el texto para que el operador copie a mano.
          window.prompt('Copia manualmente:', value);
        }
      }}
    >
      {label}
    </button>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmar',
  danger,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Spinner label="Aplicando…" /> : confirmLabel}
          </button>
        </>
      }
    >
      {message}
    </Modal>
  );
}
